import { Link, useNavigate } from "react-router-dom";
import { useCompare } from "../contexts/CompareContext";
import { Button } from "../components/ui/button";
import { X, Star, Users, Zap, Truck, Infinity as InfinityIcon, Car, ArrowRight } from "lucide-react";

/**
 * Side-by-side compare view for up to 3 vehicles the user has starred.
 * Snapshots come from CompareContext; we intentionally keep this page
 * data-light so it renders instantly — the "Ver detalles" CTA sends the
 * user to the full car page when they're ready to book.
 */
export default function CompareVehiclesPage() {
  const { items, remove, clear } = useCompare();
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-sand" data-testid="compare-page">
      <div className="container-main py-8 max-w-5xl">
        <div className="flex items-center justify-between gap-4 mb-6 flex-wrap">
          <div>
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-agave">Comparar autos</h1>
            <p className="text-sm text-neutral-600 mt-1">
              Elige hasta 3 autos y decide más rápido cuál es tu match.
            </p>
          </div>
          {items.length > 0 && (
            <Button variant="outline" size="sm" onClick={clear} data-testid="compare-clear-all">
              Vaciar lista
            </Button>
          )}
        </div>

        {items.length === 0 ? (
          <div className="card-base text-center py-16" data-testid="compare-empty">
            <Car className="w-12 h-12 text-neutral-300 mx-auto mb-4" />
            <p className="text-lg font-semibold text-agave mb-1">Aún no tienes autos para comparar</p>
            <p className="text-sm text-neutral-500 mb-6">
              Toca la estrella ⭐ en cualquier auto para añadirlo aquí.
            </p>
            <Button onClick={() => navigate("/search")} className="btn-primary" data-testid="compare-empty-search">
              Explorar autos
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <div
              className="grid gap-3 min-w-max px-4 sm:min-w-0 sm:px-0"
              style={{ gridTemplateColumns: `repeat(${items.length}, minmax(240px, 1fr))` }}
              data-testid="compare-grid"
            >
              {items.map((v) => (
                <div
                  key={v.vehicle_id}
                  className="bg-white rounded-2xl border border-neutral-200 shadow-sm overflow-hidden flex flex-col"
                  data-testid={`compare-card-${v.vehicle_id}`}
                >
                  <div className="relative aspect-[4/3] bg-neutral-100">
                    {v.photo ? (
                      <img src={v.photo} alt={`${v.make} ${v.model}`} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-neutral-300">
                        <Car className="w-12 h-12" />
                      </div>
                    )}
                    <button
                      onClick={() => remove(v.vehicle_id)}
                      className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white/95 shadow flex items-center justify-center hover:bg-white"
                      aria-label="Quitar de comparación"
                      data-testid={`compare-remove-${v.vehicle_id}`}
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="p-4 flex-1 flex flex-col">
                    <p className="font-heading font-semibold text-agave text-lg leading-tight">
                      {v.make} {v.model}
                    </p>
                    <p className="text-xs text-neutral-500">{v.year} · {v.location_city}</p>

                    <p className="text-2xl font-heading font-bold text-terracotta mt-3">
                      ${(v.daily_rate || 0).toLocaleString("es-MX")}
                      <span className="text-sm font-normal text-neutral-500 ml-1">MXN/día</span>
                    </p>

                    <div className="mt-4 space-y-2 text-sm text-neutral-700 flex-1">
                      <Row icon={Star}      label="Rating"        value={v.rating ? `${v.rating.toFixed(1)} (${v.review_count || 0})` : "Sin reseñas"} />
                      <Row icon={Users}     label="Asientos"      value={v.seats || "—"} />
                      <Row icon={Car}       label="Transmisión"   value={v.transmission === "automatic" ? "Automático" : v.transmission === "manual" ? "Manual" : (v.transmission || "—")} />
                      <Row icon={InfinityIcon} label="Kilometraje" value={v.unlimited_mileage ? "Ilimitado" : "Limitado"} yes={v.unlimited_mileage} />
                      <Row icon={Truck}     label="Entrega"       value={v.delivery_enabled ? "A domicilio" : "En sitio"} yes={v.delivery_enabled} />
                      <Row icon={Zap}       label="Reserva"       value={v.instant_book ? "Instantánea" : "Con aprobación"} yes={v.instant_book} />
                    </div>

                    <Link
                      to={`/cars/${v.vehicle_id}`}
                      className="btn-primary w-full mt-5 text-center"
                      data-testid={`compare-view-${v.vehicle_id}`}
                    >
                      Ver detalles
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value, yes }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${yes ? "text-emerald-600" : "text-neutral-400"}`} />
      <div className="flex-1 min-w-0">
        <p className="text-[11px] uppercase tracking-wider text-neutral-400">{label}</p>
        <p className="text-sm">{value}</p>
      </div>
    </div>
  );
}
